/**
 * Reports > Arrival (user, 2026-10-17): the legacy Daily Arrival Report and the ten "Arrival Report -
 * PM" reports. Same `ReportResult` shape as the RSW reports, their own endpoint so no desk lock
 * applies.
 */
import {
  ARRIVAL_TYPE_KEYS,
  type ArrivalReportSlug,
  type ReportResult,
} from "@nexseed/shared";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { downloadExport, withQuery } from "@/features/inventory/export";
import { api } from "@/lib/api-client";
import { DATE_MODES, resolveDateFilter, todayInIndia } from "@/lib/dates";
import { openPdf } from "@/lib/open-pdf";

const pathOf = (slug: ArrivalReportSlug) => `/reports/arrival/${slug}`;

const id = z.coerce.number().int().positive().optional().catch(undefined);
const name = z.string().max(120).optional().catch(undefined);

/** PM reports default to all history; the Daily report retains its day. */
export const arrivalReportSearch = z.object({
  /** All dates (none) / Today / Specific date / Date range — PM reports only. */
  dates: z.enum(DATE_MODES).optional().catch(undefined),
  from: z.iso.date().optional().catch(undefined),
  to: z.iso.date().optional().catch(undefined),
  type: z.enum(ARRIVAL_TYPE_KEYS).optional().catch(undefined),
  cropId: id,
  variety: z.string().optional().catch(undefined),
  organiser: name,
  state: name,
  location: name,
  person: name,
  sourcePlantId: id,
  vendorId: id,
});
export type ArrivalReportSearch = z.infer<typeof arrivalReportSearch>;

export function arrivalReportParams(
  s: ArrivalReportSearch,
  facilityId: number | undefined,
  slug?: ArrivalReportSlug
) {
  const today = todayInIndia();
  const { dates, ...rest } = s;
  if (slug === "daily")
    return {
      facilityId,
      ...rest,
      from: s.from ?? s.to ?? today,
      to: s.to ?? s.from ?? today,
    };
  return {
    facilityId,
    ...rest,
    ...resolveDateFilter(dates, s.from, s.to, today),
  };
}
type Params = ReturnType<typeof arrivalReportParams>;

export function useArrivalReport(
  slug: ArrivalReportSlug,
  p: Params,
  enabled = true
) {
  return useQuery({
    queryKey: ["reports", "arrival", slug, p],
    queryFn: () => api<ReportResult>(withQuery(pathOf(slug), p)),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export const arrivalReportPdf = (slug: ArrivalReportSlug, p: Params) =>
  openPdf(withQuery(pathOf(slug), { ...p, format: "pdf" }));

export const arrivalReportXlsx = (
  slug: ArrivalReportSlug,
  p: Params,
  fileName: string
) => downloadExport(pathOf(slug), { ...p, format: "xlsx" }, fileName);
